const cleanString = (value) => (typeof value === 'string' ? value.trim() : '');

const uniqueStrings = (values = []) => {
  const seen = new Set();
  return values.reduce((accumulator, value) => {
    const normalized = cleanString(value);
    if (!normalized) return accumulator;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return accumulator;
    seen.add(key);
    accumulator.push(normalized);
    return accumulator;
  }, []);
};

const SOURCE_LABEL_MAP = {
  linkedin: 'LinkedIn',
  indeed: 'Indeed',
  'employee referral': 'Employee referral',
  employee_referral: 'Employee referral',
  referral: 'Employee referral',
  ai_sourcing: 'AI sourcing',
  'ai sourcing': 'AI sourcing',
  recruitment_agency: 'Recruitment agency',
  'recruitment agency': 'Recruitment agency',
  agency: 'Recruitment agency',
  specialist_boards: 'Specialist boards',
  'specialist boards': 'Specialist boards',
  jobseeqr: 'JOBSEEQR',
  direct_application: 'JOBSEEQR',
  direct: 'JOBSEEQR',
  job_portal: 'JOBSEEQR',
  career_portal: 'JOBSEEQR',
  bulk_upload: 'JOBSEEQR',
  cv_upload: 'JOBSEEQR',
  profile_derivation: 'JOBSEEQR',
  ats_onboarding: 'JOBSEEQR',
  onboarding_manual: 'JOBSEEQR',
  frontend_ats_transition: 'JOBSEEQR',
  imported: 'JOBSEEQR'
};

const GENERIC_PLATFORM_SOURCES = new Set([
  'jobseeqr',
  'direct_application',
  'direct',
  'job_portal',
  'career_portal',
  'bulk_upload',
  'cv_upload',
  'profile_derivation',
  'ats_onboarding',
  'onboarding_manual',
  'frontend_ats_transition',
  'imported'
]);

const humanize = (value) =>
  cleanString(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const toAnalyticsSourceLabel = (value) => {
  const normalized = cleanString(value);
  if (!normalized) return '';
  const key = normalized.toLowerCase();
  return SOURCE_LABEL_MAP[key] || humanize(normalized);
};

const inferSourceCategory = ({ primarySource, rawSource, rawDetails, applicantType }) => {
  const haystack = [primarySource, rawSource, rawDetails, applicantType]
    .map((value) => cleanString(value).toLowerCase())
    .filter(Boolean)
    .join(' ');

  if (!haystack || haystack === 'jobseeqr') return 'platform';
  if (haystack.includes('ai sourcing') || haystack.includes('ai_sourcing')) return 'ai_sourcing';
  if (haystack.includes('agency')) return 'recruitment_agency';
  if (haystack.includes('referral')) return 'referral';
  if (haystack.includes('linkedin')) return 'social';
  if (haystack.includes('indeed') || haystack.includes('board')) return 'job_board';
  if (haystack.includes('utm')) return 'campaign';
  if (haystack.includes('imported') || haystack.includes('upload')) return 'import';
  return 'platform';
};

export const normalizeAnalyticsSource = ({
  utmSource = '',
  source = '',
  sourceDetails = '',
  sourceCategory = '',
  sourceTags = [],
  customTags = [],
  applicantType = ''
} = {}) => {
  const normalizedUtm = cleanString(utmSource);
  const normalizedSource = cleanString(source);
  const normalizedDetails = cleanString(sourceDetails);

  const fallbackSource = normalizedUtm
    || (
      GENERIC_PLATFORM_SOURCES.has(normalizedSource.toLowerCase())
        ? ''
        : toAnalyticsSourceLabel(normalizedSource)
    )
    || (
      GENERIC_PLATFORM_SOURCES.has(normalizedDetails.toLowerCase())
        ? ''
        : toAnalyticsSourceLabel(normalizedDetails)
    )
    || 'JOBSEEQR';

  const primarySource = fallbackSource || 'JOBSEEQR';
  const resolvedSourceCategory = cleanString(sourceCategory)
    || inferSourceCategory({
      primarySource,
      rawSource: normalizedSource,
      rawDetails: normalizedDetails,
      applicantType
    });

  const resolvedSourceTags = uniqueStrings([
    ...sourceTags,
    ...customTags,
    normalizedUtm && normalizedUtm !== primarySource ? normalizedUtm : '',
    normalizedSource && !GENERIC_PLATFORM_SOURCES.has(normalizedSource.toLowerCase()) ? toAnalyticsSourceLabel(normalizedSource) : '',
    normalizedDetails && !GENERIC_PLATFORM_SOURCES.has(normalizedDetails.toLowerCase()) ? toAnalyticsSourceLabel(normalizedDetails) : ''
  ]);

  return {
    primarySource,
    sourceCategory: resolvedSourceCategory || 'platform',
    sourceTags: resolvedSourceTags
  };
};

export const buildRoleAnalyticsFields = ({
  roleId = '',
  jobTitle = '',
  department = '',
  location = '',
  ownerRecruiterId = '',
  ownerRecruiterName = '',
  ownerRecruiterEmail = '',
  tags = [],
  sourceManpowerRequestId = ''
} = {}) => ({
  roleId: cleanString(roleId),
  jobTitle: cleanString(jobTitle),
  department: cleanString(department),
  location: cleanString(location),
  ownerRecruiterId: cleanString(ownerRecruiterId),
  ownerRecruiterName: cleanString(ownerRecruiterName),
  ownerRecruiterEmail: cleanString(ownerRecruiterEmail),
  tags: uniqueStrings(tags),
  sourceManpowerRequestId: cleanString(sourceManpowerRequestId)
});

export const buildCandidateAnalyticsFields = ({
  candidateId = '',
  roleId = '',
  jobTitle = '',
  department = '',
  location = '',
  ownerRecruiterId = '',
  ownerRecruiterName = '',
  ownerRecruiterEmail = '',
  sourceInfo = {},
  tags = []
} = {}) => ({
  candidateId: cleanString(candidateId),
  roleId: cleanString(roleId),
  jobTitle: cleanString(jobTitle),
  department: cleanString(department),
  location: cleanString(location),
  ownerRecruiterId: cleanString(ownerRecruiterId),
  ownerRecruiterName: cleanString(ownerRecruiterName),
  ownerRecruiterEmail: cleanString(ownerRecruiterEmail),
  ...normalizeAnalyticsSource(sourceInfo),
  sourceTags: uniqueStrings([...(sourceInfo?.sourceTags || []), ...tags])
});

export const buildActorAnalyticsContext = ({
  actorUserId = '',
  actorUserEmail = '',
  ownerRecruiterId = '',
  ownerRecruiterEmail = '',
  primarySource = '',
  sourceCategory = '',
  sourceTags = [],
  extra = {}
} = {}) => ({
  ...(cleanString(actorUserId) ? { actorUserId: cleanString(actorUserId) } : {}),
  ...(cleanString(actorUserEmail) ? { actorUserEmail: cleanString(actorUserEmail) } : {}),
  ...(cleanString(ownerRecruiterId) ? { ownerRecruiterId: cleanString(ownerRecruiterId) } : {}),
  ...(cleanString(ownerRecruiterEmail) ? { ownerRecruiterEmail: cleanString(ownerRecruiterEmail) } : {}),
  ...(cleanString(primarySource) ? { primarySource: cleanString(primarySource) } : {}),
  ...(cleanString(sourceCategory) ? { sourceCategory: cleanString(sourceCategory) } : {}),
  ...(Array.isArray(sourceTags) && sourceTags.length ? { sourceTags: uniqueStrings(sourceTags) } : {}),
  ...(extra && typeof extra === 'object' ? extra : {})
});
